import GoogleMapReact from "google-map-react";
import _ from "lodash";
import React, { useEffect, useRef, useState, useMemo } from "react";

import { dotblue16, dotOrange16, greend, purple, yellow6, greenNewIcon, purpleNewIcon } from "../../Assets/Images";
import { GoogleMapThemes } from "../../Assets/styles/google-map/google-maps-themes";
import Constant from "./Constants";

const markerIconsSelection = {
	"#FF00FF": purpleNewIcon,
	"#0DAF00": greenNewIcon
};

const { ALL_TENANT_PINS_COLOR, TENANT_PINS_COLOR } = Constant;
const THEME_INDEX = 0;

const { REACT_APP_GOOGLE_MAPS_API_KEY } = process.env;

const GoogleMap = React.memo(
	({
		center,
		polygons,
		markers,
		circles,
		handlePolygonClick,
		tenant_markers,
		tenant_markers_1,
		_markers,
		all_raw_tenant_markers,
		buildHeatMap,
		tenant,
		zoomToPolygon,
		handleMarkerClick,
		mapHeight
	}) => {
		const googleMapRef = useRef({
			destroy: () => {
				// no-op cleanup if needed, or you can put custom cleanup logic here
			}
		});
		const [mapState, setMapState] = useState({
			googleMap: null,
			map: null,
			mapLoaded: false,
			markers: [],
			polygons: [],
			circles: [],
			heatMap: null
		});

		const getMapOptions = maps => ({
			mapTypeControl: true,
			fullscreenControl: true,
			fullscreenControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM },
			mapTypeControlOptions: {
				style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
				position: maps.ControlPosition.RIGHT_BOTTOM, // pick a corner you like
				mapTypeIds: [
					maps.MapTypeId.ROADMAP,
					maps.MapTypeId.SATELLITE // ← include satellite
				]
			},
			streetViewControl: false,
			styles: GoogleMapThemes[THEME_INDEX],
			zoomControl: true,
			zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM }
		});

		const memoizedPolygons = useMemo(() => polygons, [polygons]);
		const memoizedMarkers = useMemo(() => markers, [markers]);
		const memoizedCircles = useMemo(() => circles, [circles]);
		const memoizedBuildHeatMap = useMemo(() => buildHeatMap, [buildHeatMap]);

		useEffect(() => {
			if (mapState.mapLoaded) {
				clearMapEntities();
				handleMapInitialization(memoizedPolygons, memoizedMarkers, memoizedCircles);
				if (memoizedBuildHeatMap) buildHeatmap(memoizedPolygons);
			}
			return () => {
				clearMapEntities();
			};
		}, [mapState.mapLoaded, memoizedPolygons, memoizedMarkers, memoizedCircles, memoizedBuildHeatMap]);

		const handleApiLoaded = ({ map, maps }) => {
			map.setOptions(getMapOptions(maps));
			setMapState(prevState => ({
				...prevState,
				map,
				googleMap: maps,
				mapLoaded: true
			}));
		};

		const handleMapInitialization = (polygons, markers, circles) => {
			if (!_.isEmpty(polygons)) buildPolygons(polygons);
			if (!_.isEmpty(markers)) buildMarkers(markers);
			if (!_.isEmpty(circles)) buildCircles(circles);
		};

		const buildCircles = circleData => {
			const { googleMap, map } = mapState;
			const circleInstances = circleData?.filter(Boolean)?.map(circle => {
				const circleInstance = new googleMap.Circle({
					center: circle.center,
					radius: circle.radius,
					strokeColor: circle.style?.strokeColor || "#000",
					strokeOpacity: circle.style?.strokeOpacity || 0.8,
					strokeWeight: circle.style?.strokeWeight || 2,
					fillColor: circle.style?.fillColor || "#FF0000",
					fillOpacity: circle.style?.fillOpacity || 0.35,
					map
				});

				return circleInstance;
			});

			setMapState(prevState => ({ ...prevState, circles: circleInstances }));
		};

		const buildHeatmap = polygonsData => {
			const { googleMap, map } = mapState;
			if (!googleMap || !map) return;

			const heatmapPoints = Object.values(polygonsData)
				.map(polygon => {
					if (!polygon.marker?.position?.lat || !polygon.marker?.position?.lng) {
						console.error("Invalid polygon marker position", polygon);
						return null;
					}
					return {
						location: new googleMap.LatLng(polygon.marker.position.lat, polygon.marker.position.lng),
						weight: polygon.data.weight || 1
					};
				})
				.filter(Boolean);

			const heatmapLayer = new googleMap.visualization.HeatmapLayer({
				data: heatmapPoints,
				radius: 20
			});

			heatmapLayer.setMap(map);
			setMapState(prevState => ({ ...prevState, heatMap: heatmapLayer }));
		};

		const buildPolygons = data => {
			const { googleMap, map } = mapState;
			const bounds = new googleMap.LatLngBounds();

			const getZoomLevel = map.getZoom();
			const chunkSize = getZoomLevel < 10 ? 50000 : 10000;
			const chunkedData = _.chunk(Object.values(data), chunkSize); // Adjust chunk size based on zoom level

			const polygons = chunkedData.flatMap(chunk => {
				return chunk.map(polygonData => {
					const polygon = new googleMap.Polygon({
						paths: polygonData.coordinates,
						strokeColor: polygonData.style?.strokeColor || "#000",
						fillColor: polygonData.style?.fillColor || "#ffffff",
						fillOpacity: 0.75,
						strokeWeight: 1
					});

					if (polygonData.polygonClick) {
						polygon.addListener("click", () => {
							if (handlePolygonClick) {
								handlePolygonClick({ _id: polygonData.id, type: polygonData.type, polygonData });
							}
						});
					}

					polygon.getPath()?.forEach(coord => bounds.extend(coord));
					polygon.setMap(map);
					return polygon;
				});
			});

			// Comment out the following line to prevent zoom level from changing automatically
			if (zoomToPolygon) map.fitBounds(bounds);

			setMapState(prevState => ({ ...prevState, polygons }));
		};

		const buildMarkers = markers => {
			const { googleMap, map } = mapState;
			const {
				census_Shops,
				tenant_Shops,
				all_tenant_markers,
				storeScoring,
				storeScoringMarkers,
				tenant_Shops_1
			} = markers;
			let allMarkers = [];

			if (census_Shops) {
				allMarkers = allMarkers.concat(Object.values(_markers).map(marker => ({ ...marker, type: "census" })));
			}
			if (tenant_Shops) {
				allMarkers = allMarkers.concat(
					Object.values(tenant_markers).map(marker => ({ ...marker, type: "tenant" }))
				);
			}
			if (all_tenant_markers) {
				allMarkers = allMarkers.concat(
					Object.values(all_raw_tenant_markers).map(marker => ({ ...marker, type: "all_tenant" }))
				);
			}
			if (storeScoring) {
				allMarkers = allMarkers.concat(
					Object.values(storeScoringMarkers).map(marker => ({ ...marker, type: "storeScoring" }))
				);
			}

			if (tenant_Shops_1) {
				allMarkers = allMarkers.concat(
					Object.values(tenant_markers_1).map(marker => ({ ...marker, type: "tenant1" }))
				);
			}

			const validMarkers = allMarkers.filter(markerData => {
				if (!markerData?.position?.lat || !markerData?.position?.lng) {
					console.error("Invalid marker position", markerData);
					return false;
				}
				return true;
			});
			// purple.png
			const markerInstances = validMarkers.map(markerData => {
				const temporaryClosed = markerData?.data?.business_status === "CLOSED TEMPORARILY";
				let missmatched = markerData?.data?.shop_matched === "Yes";
				const markerColor = markerData?.color;
				const icon = markerIconsSelection[markerColor]
					? markerIconsSelection[markerColor]
					: markerData.type === "census"
					? missmatched
						? greend
						: temporaryClosed
						? purple
						: dotblue16
					: markerData.type === "tenant"
					? TENANT_PINS_COLOR(tenant)
						? {
								path: googleMap.SymbolPath.CIRCLE,
								fillColor: TENANT_PINS_COLOR(tenant),
								fillOpacity: 1.0,
								strokeWeight: 0,
								scale: 3.4
						  }
						: dotOrange16
					: markerData.type === "storeScoring"
					? {
							path: googleMap.SymbolPath.CIRCLE,
							fillColor: "#FF0000",
							fillOpacity: 1.0,
							strokeWeight: 0,
							scale: Number(markerData.score) * 10
					  }
					: markerData.type === "tenant1"
					? yellow6
					: {
							path: googleMap.SymbolPath.CIRCLE,
							fillColor: ALL_TENANT_PINS_COLOR(tenant),
							fillOpacity: 1.0,
							strokeWeight: 0,
							scale: 3.4
					  };

				const marker = new googleMap.Marker({
					position: {
						lat: markerData.position.lat,
						lng: markerData.position.lng
					},
					icon
				});

				if (markerData.showInfo) {
					// const infoWindow = new googleMap.InfoWindow({
					// 	content: createInfoWindowContent(markerData, handleMarkerClick)
					// });

					marker.addListener("click", () => {
						// infoWindow.open(map, marker);
						handleMarkerClick(markerData);
					});
				}

				marker.setMap(map);
				return marker;
			});

			setMapState(prevState => ({ ...prevState, markers: markerInstances }));
		};

		const clearMapEntities = () => {
			const { markers, polygons, circles, heatMap } = mapState;

			markers.forEach(marker => marker.setMap(null));
			polygons.forEach(polygon => polygon.setMap(null));
			circles.forEach(circle => circle.setMap(null));
			if (heatMap) heatMap.setMap(null);

			setMapState(prevState => ({
				...prevState,
				markers: [],
				polygons: [],
				circles: [],
				heatMap: null
			}));
		};

		mapHeight = mapHeight ?? Math.floor((window.innerWidth / 1.7778) * 0.6);

		return (
			<div style={{ height: mapHeight }}>
				<GoogleMapReact
					ref={googleMapRef}
					bootstrapURLKeys={{
						key: REACT_APP_GOOGLE_MAPS_API_KEY,
						libraries: ["visualization"]
					}}
					defaultCenter={{ lat: 25.2, lng: 67.57 }}
					center={center}
					defaultZoom={7}
					onGoogleApiLoaded={handleApiLoaded}
					yesIWantToUseGoogleMapApiInternals
				/>
			</div>
		);
	}
);

export default GoogleMap;
